import { Controller } from "@hotwired/stimulus"
import { fetchWithCSRF } from "../lib/fetch"
import { register } from "../lib/pending_saves"
import { acceptedSave, saveNote } from "../lib/note_saves"

export default class extends Controller {
    static values = { id: String, state: String, version: String, key: String }
    static targets = ["display", "editor", "lockButton", "actions", "timestamp"]

    saveTimer = null
    blurTimer = null
    saving = null // tracks in-flight save promise
    #retryTimer = null
    #errorShown = false
    #unregister = null
    #createUrl = null

    connect() {
        // Identity for a note the server hasn't numbered yet. Survives in the
        // DOM across a Turbo visit, which is what lets a restored card claim the
        // save its predecessor started — see lib/note_saves.js.
        if (!this.keyValue) this.keyValue = crypto.randomUUID()
        // Captured here because a card is often detached from the list by the
        // time it flushes, and a create still needs the list's endpoint.
        this.#createUrl = this.notesList?.getCreateUrl()
        this.#adoptAcceptedSave()

        this.boundOnChange = this.onEditorChanged.bind(this)
        this.boundOnBlur = this.onEditorBlur.bind(this)
        this.boundOnFocus = this.onEditorFocus.bind(this)
        this.element.addEventListener("crepe:change", this.boundOnChange)
        this.element.addEventListener("crepe:blur", this.boundOnBlur)
        this.element.addEventListener("crepe:focus", this.boundOnFocus)
        this.#unregister = register(this)
    }

    disconnect() {
        this.#handOffSave()
        if (this.saveTimer) clearTimeout(this.saveTimer)
        if (this.blurTimer) clearTimeout(this.blurTimer)
        if (this.#retryTimer) clearTimeout(this.#retryTimer)
        this.element.removeEventListener("crepe:change", this.boundOnChange)
        this.element.removeEventListener("crepe:blur", this.boundOnBlur)
        this.element.removeEventListener("crepe:focus", this.boundOnFocus)
        if (this.#unregister) {
            this.#unregister()
            this.#unregister = null
        }
    }

    // Registry contract — a save that is queued, in flight, or waiting to
    // retry. A note the server rejected as stale counts as unresolved instead;
    // resending the same body would just be rejected again.
    hasPending() {
        return Boolean(this.saveTimer || this.saving || this.#retryTimer)
    }

    // Registry contract — local editor content the server hasn't accepted.
    // Broader than hasPending: also true after a non-retryable failure
    // (412 stale, 403 dead session, etc) where #errorShown stays set until
    // a successful save lands. Navigation guards check this so a stuck
    // failure isn't silently discarded by Turbo replacing the DOM.
    hasUnresolved() {
        return this.hasPending() || this.#errorShown
    }

    // Registry contract — drives queued work to completion and reports
    // whether the local state converged with the server. A `false` return
    // tells the caller (turbo:before-visit) that some edit is still ahead
    // of the server so it can prompt before navigating away.
    async flushPending() {
        if (this.#retryTimer) {
            clearTimeout(this.#retryTimer)
            this.#retryTimer = null
        }
        if (this.saveTimer) {
            clearTimeout(this.saveTimer)
            this.saveTimer = null
            await this.save()
        }
        if (this.saving) {
            await this.saving
        }
        return !this.hasUnresolved()
    }

    // Registry contract — pagehide, where only a keepalive request survives the
    // unload.
    flushBeacon() {
        this.#handOffSave({ keepalive: true })
    }

    // Send the outstanding edit and let note_saves keep the result: by the time
    // this card is torn down there is nobody left to report back to.
    #handOffSave({ keepalive = false } = {}) {
        if (!this.hasPending()) return
        const content = this.getEditorMarkdown()
        if (!content.trim()) return
        saveNote({ ...this.#saveRequest(content), keepalive }).catch(() => {})
    }

    #saveRequest(content) {
        return {
            key: this.keyValue,
            id: this.idValue,
            version: this.versionValue,
            content,
            createUrl: this.#createUrl,
        }
    }

    // Turbo cached this card's DOM before the last save finished, so its id and
    // version can both be behind what the server has.
    #adoptAcceptedSave() {
        const record = acceptedSave(this.idValue || this.keyValue)
        if (!record) return
        if (record.id) this.idValue = record.id
        if (record.version) this.versionValue = record.version
    }

    // The list always offers one empty card to write in, so a card leaving that
    // role asks for its replacement at the transition itself — the one moment a
    // teardown can't cancel.
    stateValueChanged(value, previous) {
        if (previous === "placeholder" && value !== "placeholder") {
            this.notesList?.ensurePlaceholder(this.element)
        }
    }

    get notesList() {
        const el = this.element.closest("[data-controller~='notes-list']")
        if (!el) return null
        return this.application.getControllerForElementAndIdentifier(el, "notes-list")
    }

    // Debounce blur to avoid false triggers from toolbar focus changes
    onEditorBlur() {
        if (this.blurTimer) clearTimeout(this.blurTimer)
        this.blurTimer = setTimeout(() => this.onBlur(), 150)
    }

    onEditorFocus() {
        // Cancel pending blur — focus returned (e.g. toolbar interaction)
        if (this.blurTimer) {
            clearTimeout(this.blurTimer)
            this.blurTimer = null
        }
        this.onFocus()
    }

    onFocus() {
        if (this.stateValue === "placeholder") {
            // Remove faded appearance
            const inner = this.element.querySelector(".opacity-60")
            if (inner) {
                inner.classList.remove("opacity-60")
            }
        }
    }

    // Fed by crepe:change, which the editor emits for every document change as
    // it lands — a keystroke, a paste, an undo, a toolbar button.
    onEditorChanged() {
        const state = this.stateValue
        if (state === "placeholder") {
            if (!this.element.querySelector(".ProseMirror")?.textContent.trim()) return
            this.stateValue = "draft"
        } else if (state === "saved") {
            this.stateValue = "dirty"
        } else if (state !== "draft" && state !== "dirty" && state !== "editing") {
            return
        }
        this.notesList?.syncStart(this.keyValue)
        this.scheduleSave()
    }

    scheduleSave() {
        if (this.saveTimer) clearTimeout(this.saveTimer)
        if (this.#retryTimer) { clearTimeout(this.#retryTimer); this.#retryTimer = null }
        this.saveTimer = setTimeout(() => this.save(), 500)
    }

    #scheduleRetry() {
        if (this.#retryTimer) return
        this.#retryTimer = setTimeout(() => {
            this.#retryTimer = null
            this.save()
        }, 3000)
    }

    async save() {
        this.saveTimer = null
        // Serialize: wait for any in-flight save to finish first.
        // This prevents duplicate CREATEs when the debounce fires while
        // a previous create is still in flight.
        if (this.saving) {
            await this.saving
            // Re-register sync so the previous save's cleanup doesn't
            // flip the indicator to "Saved" while we're about to save again
            this.notesList?.syncStart(this.keyValue)
        }

        const markdown = this.getEditorMarkdown()

        // Don't create empty notes — clear sync if it was started
        if (!markdown.trim() && !this.idValue) {
            this.notesList?.syncDone(this.keyValue)
            return
        }

        this.saving = this.#send(markdown)
        const result = await this.saving
        this.saving = null

        if (!result.ok) {
            // Show error toast once per error streak
            if (!this.#errorShown) {
                this.#errorShown = true
                this.#showErrorToast(result.description)
            }

            if (result.retryable) {
                // Keep indicator on "Saving..." and retry
                this.#scheduleRetry()
            } else {
                this.notesList?.syncError(this.keyValue)
            }
            return
        }

        // Success — clear error state
        this.#errorShown = false

        // Only flip to "Saved" when no more saves are queued
        if (!this.saveTimer) {
            this.notesList?.syncDone(this.keyValue)
        } else {
            // More saves pending — make sure the current key stays in the map
            this.notesList?.syncStart(this.keyValue)
        }
    }

    #showErrorToast(description) {
        document.dispatchEvent(new CustomEvent("basecoat:toast", {
            detail: {
                config: {
                    category: "error",
                    title: "Save failed",
                    description,
                    duration: -1,
                    cancel: { label: "Dismiss" },
                }
            }
        }))
    }

    #saveFailure(status, description) {
        return {
            ok: false,
            retryable: status === 408 || status === 425 || status === 429 || status >= 500,
            description,
        }
    }

    #responseFailure(status) {
        if (status === 409) {
            return this.#saveFailure(status, "This thread is archived and can no longer be edited.")
        }
        if (status === 412) {
            return this.#saveFailure(status, "This note was changed in another tab or device. Reload the page to see the latest version.")
        }
        if (status === 403) {
            return this.#saveFailure(status, "Your session may have expired. Reload the page and try again.")
        }
        if (status === 408 || status === 425 || status === 429 || status >= 500) {
            return this.#saveFailure(status, "Could not save right now. Retrying automatically...")
        }
        return this.#saveFailure(status, "The server rejected the save. Reload the page and try again.")
    }

    // Saves, then applies what the server accepted back onto the card. save()
    // owns the sync indicator.
    async #send(markdown) {
        try {
            const created = !this.idValue
            const result = await saveNote(this.#saveRequest(markdown))
            if (!result.ok) {
                console.error("Error saving note:", result.status)
                return this.#responseFailure(result.status)
            }
            this.idValue = result.id
            this.versionValue = result.version
            if (this.stateValue === "draft" || this.stateValue === "dirty") {
                this.stateValue = "saved"
            }
            if (created) this.addNoteActions(result.createdAt)
            return { ok: true }
        } catch (error) {
            console.error("Error saving note:", error)
            return {
                ok: false,
                retryable: true,
                description: "Could not save right now. Retrying automatically...",
            }
        }
    }

    // An emptied note is kept: the trash button, with its confirm, is how a
    // note gets removed.
    async onBlur() {
        if (this.saveTimer) {
            clearTimeout(this.saveTimer)
            await this.save()
        }
        if (this.saving) await this.saving

        // Untouched placeholder — restore its faded appearance.
        if (this.stateValue === "placeholder") {
            this.element.children[0]?.classList.add("opacity-60")
        }
    }

    closeDeleteConfirm() {
        const trigger = this.element.querySelector(".dropdown-menu button[aria-expanded]")
        if (trigger) trigger.click()
    }

    async confirmDelete() {
        if (!this.idValue) {
            this.element.remove()
            return
        }

        try {
            const response = await fetchWithCSRF(`/frame/notes/${this.idValue}`, {
                method: "DELETE",
            })
            if (response.ok) {
                this.notesList?.ensurePlaceholder(this.element)
                this.element.remove()
            }
        } catch (error) {
            console.error("Error deleting note:", error)
        }
    }

    toggleLock() {
        if (this.stateValue === "readonly") {
            this.unlock()
        } else if (this.stateValue === "editing") {
            this.lock()
        }
    }

    unlock() {
        if (!this.hasDisplayTarget || !this.hasEditorTarget) return

        const markdown = this.displayTarget.dataset.markdown || ""
        this.displayTarget.classList.add("hidden")
        this.editorTarget.classList.remove("hidden")

        // Inject a CrepeEditor — Stimulus will auto-initialize it
        this.editorTarget.innerHTML = `
            <div class="crepe-editor-wrapper"
                 data-controller="crepe-editor"
                 data-crepe-editor-content-value="${this.escapeAttr(markdown)}"
                 data-crepe-editor-placeholder-value="">
                <div data-crepe-editor-target="editor"></div>
            </div>
        `
        this.stateValue = "editing"

        // Update lock icon to open
        if (this.hasLockButtonTarget) {
            this.lockButtonTarget.innerHTML = ""
            const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
            svg.setAttribute("class", "w-3.5 h-3.5")
            const use = document.createElementNS("http://www.w3.org/2000/svg", "use")
            use.setAttribute("href", "/img/icons.svg#lucide--lock-open")
            svg.appendChild(use)
            this.lockButtonTarget.appendChild(svg)
        }
    }

    async lock() {
        // Flush any pending save
        if (this.saveTimer) {
            clearTimeout(this.saveTimer)
            await this.save()
        }
        if (this.saving) await this.saving

        // Delegate stream reload to parent controller
        this.notesList?.reloadStream()
    }

    addNoteActions(createdAt) {
        if (this.hasTimestampTarget) {
            this.timestampTarget.textContent = createdAt
        }
        if (this.hasActionsTarget) {
            this.actionsTarget.classList.remove("hidden")
        }
    }

    async openAssign() {
        const frame = document.createElement("turbo-frame")
        frame.id = `assign-dialog-frame-${this.idValue}`
        frame.src = `/frame/notes/${this.idValue}/assign-dialog`
        this.element.appendChild(frame)
    }

    async assignToThread(event) {
        const threadId = event.currentTarget.dataset.threadId
        event.currentTarget.closest("dialog").close()
        const response = await fetchWithCSRF(`/frame/notes/${this.idValue}/assign`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ thread_id: threadId }),
        })
        if (response.ok) {
            this.notesList?.ensurePlaceholder(this.element)
            this.element.remove()
        }
    }

    // Backdate this note. The datepicker fires `change` with the picked day;
    // we flush any in-flight content edit first (so its version is current and
    // unsaved content isn't dropped by the reload), PATCH the date, then reload
    // the stream because the note may jump to another day and flip read-only.
    async saveDate(event) {
        const date = event.currentTarget.value
        if (!date || !this.idValue) return

        if (this.saveTimer) {
            clearTimeout(this.saveTimer)
            await this.save()
        }
        if (this.saving) await this.saving

        const url = `/frame/notes/${this.idValue}/date`
        const body = JSON.stringify({ date, version: this.versionValue })
        try {
            let response = await fetchWithCSRF(url, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body,
            })
            if (response.status === 403) {
                response = await fetchWithCSRF(url, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body,
                })
            }
            if (response.ok) {
                this.notesList?.reloadStream()
                return
            }
            console.error("Error saving note date:", response.status)
            this.#showErrorToast(this.#responseFailure(response.status).description)
        } catch (error) {
            console.error("Error saving note date:", error)
            this.#showErrorToast("Could not change the date right now. Reload and try again.")
        }
    }

    getEditorMarkdown() {
        const editorEl = this.element.querySelector("[data-controller='crepe-editor']")
        if (!editorEl) return ""
        const ctrl = this.application.getControllerForElementAndIdentifier(
            editorEl,
            "crepe-editor"
        )
        // After teardown the value attribute holds the surviving copy — the
        // same one Turbo cached.
        return ctrl?.getMarkdown() || editorEl.dataset.crepeEditorContentValue || ""
    }

    escapeAttr(s) {
        return s
            .replace(/&/g, "&amp;")
            .replace(/"/g, "&quot;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
    }
}
